import dayjs from 'dayjs';
import i18n, { type TFunction, type i18n as I18n } from 'i18next';
import i18nextHTTPBackend from 'i18next-http-backend';
import { isValid } from './oid';

export type CreateClientReturn = { i18n: I18n; initPromise: Promise<TFunction> };

// see https://github.com/i18next/i18next/blob/master/src/PluralResolver.js#L109-L119
function getPluralRules(code: string, options: { ordinal?: boolean } = {}) {
    if (this.shouldUseIntlApi()) {
        try {
            return new Intl.PluralRules(isValid(code) ? 'en' : code, {
                type: options.ordinal ? 'ordinal' : 'cardinal',
            });
        } catch {
            // eslint-disable-next-line consistent-return
            return;
        }
    }

    return this.rules[code] || this.rules[this.languageUtils.getLanguagePartFromCode(code)];
}

export default (language: string, languageCaches?: { [languageKey: string]: string }): CreateClientReturn => {
    const instance = i18n
        .createInstance({
            defaultNS: 'common',
            fallbackLng: [],
            ns: [],
            load: 'currentOnly',
            lng: language,

            interpolation: {
                escapeValue: false,
                format: (value: any, format: string) => {
                    if (value instanceof Date) {
                        return dayjs(value).format(format);
                    }

                    return value;
                },
                formatSeparator: ',',
            },

            backend: {
                allowMultiLoading: false,
                loadPath: (languages: string[], namespaces: string[]) => {
                    const language = languages[0];
                    let path = `/.proxy/translations/${language}/${namespaces[0]}.json`;

                    const hash = languageCaches && languageCaches[language];
                    if (hash) {
                        path += `?v=${hash}`;
                    }

                    return path;
                },
            } as object,

            react: {
                useSuspense: true,
            },
        })
        .use(i18nextHTTPBackend);

    const initPromise = instance.init(() => {
        // override plural rules
        instance.services.pluralResolver.getRule = getPluralRules;
    });

    return { i18n: instance, initPromise };
};
